import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight } from 'lucide-react';

const STORAGE_KEY = 'mw-last-content';

export interface ContentVisit {
  path: string;
  title: string;
  category: 'bikes' | 'cars' | 'guides' | 'data' | 'ev' | 'scooters';
  visitedAt: string;
}

const categoryLabels: Record<ContentVisit['category'], string> = {
  bikes: 'Bikes',
  cars: 'Cars',
  guides: 'Guide',
  data: 'Data',
  ev: 'EV',
  scooters: 'Scooters',
};

const categoryColors: Record<ContentVisit['category'], string> = {
  bikes: 'text-cat-motorcycles bg-cat-motorcycles/10',
  cars: 'text-cat-cars bg-cat-cars/10',
  guides: 'text-emerald-500 bg-emerald-500/10',
  data: 'text-blue-500 bg-blue-500/10',
  ev: 'text-green-500 bg-green-500/10',
  scooters: 'text-amber-500 bg-amber-500/10',
};

/** Detect content category from pathname */
export function detectContentCategory(pathname: string): ContentVisit['category'] | null {
  if (pathname.startsWith('/bikes/') || pathname.startsWith('/bikes.')) return 'bikes';
  if (pathname.startsWith('/cars/') || pathname.startsWith('/cars.')) return 'cars';
  if (pathname.startsWith('/guides/') || pathname.startsWith('/guides.')) return 'guides';
  if (pathname.startsWith('/data/') || pathname.startsWith('/data.')) return 'data';
  if (pathname.startsWith('/ev/') || pathname.startsWith('/ev.')) return 'ev';
  if (pathname.startsWith('/scooters')) return 'scooters';
  return null;
}

/** Record a content page visit in localStorage */
export function trackContentVisit(path: string, title: string, category: ContentVisit['category']): void {
  const visit: ContentVisit = { path, title, category, visitedAt: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(visit));
  window.dispatchEvent(new Event('content-visit-update'));
}

function getLastVisit(): ContentVisit | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ContentVisit;
  } catch {
    return null;
  }
}

/** Strip " | MotoWalla" or " — ... | MotoWalla" suffix for a clean display title */
function cleanTitle(title: string): string {
  return title
    .replace(/\s*[|—–]\s*MotoWalla$/i, '')
    .replace(/\s*[|—–]\s*Complete Guide$/i, '')
    .trim();
}

export function ContinueLearningWidget() {
  const [visit, setVisit] = useState<ContentVisit | null>(getLastVisit);

  useEffect(() => {
    const handler = () => setVisit(getLastVisit());
    window.addEventListener('content-visit-update', handler);
    return () => window.removeEventListener('content-visit-update', handler);
  }, []);

  if (!visit) return null;

  const label = categoryLabels[visit.category];
  const colors = categoryColors[visit.category] ?? 'text-primary-500 bg-primary-500/10';
  const displayTitle = cleanTitle(visit.title);

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-teal-500/5 to-transparent">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-500/10">
          <BookOpen className="h-4 w-4 text-teal-500" />
        </div>
        <h3 className="font-semibold text-text-primary text-sm">Continue Reading</h3>
      </div>

      <Link
        to={visit.path}
        className="block px-4 py-3 transition-colors hover:bg-bg-tertiary group"
      >
        <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${colors}`}>
          {label}
        </span>
        <p className="mt-1.5 text-sm font-medium text-text-secondary group-hover:text-primary-400 transition-colors line-clamp-2">
          {displayTitle}
        </p>
        <div className="mt-2 flex items-center gap-1 text-xs font-medium text-primary-500">
          Continue
          <ChevronRight className="h-3 w-3" />
        </div>
      </Link>
    </div>
  );
}
